import type { RefObject } from "react";
import { useMemo, useRef, useState } from "react";

import { debounce } from "lodash";

import { Search, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { DotsLoader, Popover, PopoverTrigger, Tooltip, Type } from "@similarweb/ui-components";
import { TextField } from "@similarweb/ui-components/forms";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import CompanyAutocompleteItem from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteItem/CompanyAutocompleteItem";
import NoSearchResults from "app/pages/sales-intelligence/modules/company-autocomplete/components/NoSearchResults/NoSearchResults";
import useCompanyAutocompleteQuery from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteQuery";
import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";

import {
    StyledAutocompleteTrigger,
    StyledAutocompleteSearch,
    StyledAutocompleteIcon,
    StyledPopoverContent,
    StyledAutocompleteTriggerText,
} from "./styles";

const DEFAULT_RECENTS: IAutoCompleteSiteItem[] = [];

const AICompanyAutocomplete = ({
    recents = DEFAULT_RECENTS,
    onSelect,
    selectedItem,
    isOpenByDefault = true,
    isDisabled = false,
}: {
    recents?: IAutoCompleteSiteItem[];
    onSelect: (item: AiAgentEmailAutocompleteCompany) => void;
    selectedItem: AiAgentEmailAutocompleteCompany | null;
    isOpenByDefault?: boolean;
    isDisabled?: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(isOpenByDefault && !isDisabled);
    const [term, setTerm] = useState("");
    const triggerButtonRef = useRef<HTMLDivElement | null>(null);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);

    const { data, isLoading } = useCompanyAutocompleteQuery(term);
    const debouncedSetTerm = useMemo(() => debounce(setTerm, 250), []);

    const cleanedTerm = term.toLowerCase().replace(/,/g, " ");

    const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);

    const setTextRef = (node: HTMLDivElement | null) => {
        if (node) {
            setIsTooltipEnabled(node.scrollWidth > node.clientWidth);
        }
    };

    const onCompanyClick = ({ key, companyName, image }: { key: string; companyName: string; image?: string }) => {
        setIsOpen(false);
        setTerm("");
        onSelect({ domain: key, companyName, image, isCompanyLevel: true });
        AIAgentTrackingService.email.trackSelectCompany(companyName);
    };

    const onWebsiteClick = (domain: string, companyName?: string, image?: string) => {
        setIsOpen(false);
        setTerm("");
        onSelect({ domain, companyName: companyName || "", image, isCompanyLevel: false });
        AIAgentTrackingService.email.trackSelectCompany(domain);
    };

    const handleToggle = (_isOpen: boolean, event?: Event) => {
        const isWithinSearch = inputWrapperRef.current?.contains(event?.target as Node);

        if (isWithinSearch) {
            return;
        }

        setIsOpen((prev) => !prev);
        if (selectedItem) {
            setTerm("");
        }
    };

    const renderSearchResults = () => {
        if (term) {
            if (Array.isArray(data) && data.length === 0) {
                return <NoSearchResults searchTerm={term} />;
            }
            if (Array.isArray(data)) {
                return data.map((item: IAutoCompleteSiteItem) => (
                    <CompanyAutocompleteItem
                        key={item.name}
                        company={item}
                        hideLabel
                        onCompanyClick={onCompanyClick}
                        onWebsiteClick={onWebsiteClick}
                        queryWords={cleanedTerm.split(" ").filter(Boolean)}
                        isHideDataCreditChips
                    />
                ));
            }
            return null;
        }
        if (recents?.length) {
            return recents.map((recent, index) =>
                recent.companyInfo ? (
                    <CompanyAutocompleteItem
                        key={index}
                        company={recent}
                        hideLabel
                        onCompanyClick={onCompanyClick}
                        onWebsiteClick={onWebsiteClick}
                        isHideDataCreditChips
                    />
                ) : null,
            );
        }
        return null;
    };

    const isSearchFieldVisible = isOpen || !selectedItem;

    return (
        <Box maxHeight="40px">
            <Tooltip
                enabled={isTooltipEnabled}
                content={selectedItem?.isCompanyLevel ? selectedItem?.companyName : selectedItem?.domain}
                placement="top"
            >
                <div>
                    <StyledAutocompleteTrigger
                        ref={triggerButtonRef}
                        onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
                        isVisible={!isSearchFieldVisible}
                        isDisabled={isDisabled}
                    >
                        <StyledAutocompleteIcon
                            iconType={Type.Website}
                            iconSrc={selectedItem?.image || ""}
                            isDisabled={isDisabled}
                        />
                        <StyledAutocompleteTriggerText
                            ref={setTextRef}
                            isDisabled={isDisabled}
                        >
                            {selectedItem?.isCompanyLevel ? selectedItem?.companyName : selectedItem?.domain}
                        </StyledAutocompleteTriggerText>
                        <Flex margin="0 0 0 auto">
                            <SWReactIcons
                                iconName="arrow"
                                iconColor={isDisabled ? colorsPalettes.midnight[100] : colorsPalettes.carbon[400]}
                            />
                        </Flex>
                    </StyledAutocompleteTrigger>
                </div>
            </Tooltip>
            {isSearchFieldVisible ? (
                <StyledAutocompleteSearch
                    ref={inputWrapperRef}
                    onClick={() => {
                        if (!isDisabled) {
                            setIsOpen(true);
                        }
                    }}
                >
                    <TextField
                        autoFocus
                        placeholder={translate("si.pages.ai_agent_email.company_autocomplete.placeholder")}
                        startAddon={<Search size="sm" />}
                        type="text"
                        onChange={(value: string) => {
                            if (!isDisabled) {
                                debouncedSetTerm(value);
                            }
                        }}
                        isDisabled={isDisabled}
                        endAddon={isLoading ? <DotsLoader /> : null}
                    />
                </StyledAutocompleteSearch>
            ) : null}

            <Popover
                isClickOutsideEnabled
                open={isOpen}
                onOpenChange={handleToggle}
                placement="bottom-start"
                initialFocus={-1}
            >
                <PopoverTrigger elementRef={triggerButtonRef as RefObject<HTMLElement>} />
                <StyledPopoverContent>{renderSearchResults()}</StyledPopoverContent>
            </Popover>
        </Box>
    );
};

export default AICompanyAutocomplete;
